<script setup lang="ts">
import BaseForm from "~/components/ui/BaseForm.vue";
import ProgressBar from "~/components/ui/ProgressBar.vue";
import Validator from "~/utils/validator";
import RadioButton from "~/components/inputs/RadioButton.vue";
import { useVuelidate } from "@vuelidate/core";
import Accordion from "~/components/ui/Accordion.vue";
import BackButton from "~/components/inputs/BackButton.vue";
import EmailAutocomplete from "~/components/inputs/EmailAutocomplete.vue";
import { searchEmail } from "~/utils/searchAutocomplete";
import emailPrivacyPep from "~/rules/emailPrivacyPep";
import SelectedAmount from "~/components/ui/SelectedAmount.vue";
import Button from "~/components/inputs/Button.vue";

const iban = await getLocale("forms", "payment_data");
const locale = await getLocale("forms", "email_privacy");

const emit = defineEmits(["init", "submit", "goBack"]);

defineComponent({
  name: "EmailPrivacyBanks",
});

defineProps({
  progress: { type: Number, required: true },
  title: { type: String, required: true },
});

const value = useState<number>("credit");
const email = useState<string>("email", () => "");
const privacy1 = useState<boolean | null>("privacy1", () => null);
const privacy2 = useState<boolean | null>("privacy2", () => null);
const privacy3 = useState<boolean | null>("privacy3", () => null);
const privacy4 = useState<boolean | null>("privacy4", () => null);
const politicallyExposedPerson = useState<boolean>("politicallyExposedPerson");

const val1 = ref("Non è possibile proseguire senza questo consenso.");
const val2 = ref(
  "Attenzione! Se scegli “no” non potrai partecipare alle nostre attività promozionali, ai concorsi, né ricevere offerte da Cofidis per i propri prodotti o servizi o per quelli di società terze.",
);
const val3 = ref(
  "Attenzione! Se scegli “no” non potremo proporti offerte e servizi personalizzati che tengano conto delle tue esigenze ed interessi specifici.",
);
const val4 = ref(
  "Attenzione! Se scegli “no” non potrai ricevere le offerte speciali da parte dei nostri partner.",
);
const politicsVal = ref(
  "Per favore, verifica la definizione di Persona Politicamente Esposta",
);

const v$ = useVuelidate(emailPrivacyPep, {
  email,
  privacy1,
  privacy2,
  privacy3,
  privacy4,
  politicallyExposedPerson,
});

const validate = () => {
  v$.value.$touch();
  v$.value.$validate();
  return !v$.value.$error;
};
const submit = () => {
  if (validate()) {
    emit("submit");
  }
};

onMounted(() => emit("init"));
</script>

<template>
  <BaseForm>
    <template #upper>
      <ProgressBar v-if="progress" :value="progress" />
      <form class="form-container mt-4" @submit.prevent="submit">

        <SelectedAmount :value="value" :custom-class="'text-xl'">
          <template v-if="$slots.header" #header>
            <slot name="header" />
          </template>
        </SelectedAmount>

        <div class="flex flex-wrap text-gray-700 p-4 pb-4 px-4 sm:px-6 lg:px-12  rounded-b-lg">
          <p class="pageTitle">
            {{ locale?.title }}
          </p>
          <p class="pageSubtitle" v-html="locale.subtitle" />
          <div class="w-full lg:w-1/2 lg:pr-2 mb-6">
            <EmailAutocomplete v-model="email" :uppercase="false" :searchFunction="searchEmail" :label="locale.email"
              :error="v$.email.$error" :error-message="Validator.message(v$.email)" test-id="email" :type="'email'" />
              <div class="text-sm italic" v-html="locale.email_label"/>
          </div>

          <div class="mb-6">
            <RadioButton v-model="privacy1" :values="yesNoOptions"
              :error="v$.privacy1.$error || (privacy1 !== null && !privacy1)" :error-message="v$.privacy1.$error ? Validator.message(v$.privacy1) : val1
                " data-testid="privacy1">
              <div class="w-auto mb-2 text-sm" v-html="'Lette e comprese <a target=\'_blank\' href=\'https://www.cofidis.it/it/download/Informativa-Privacy.pdf\' class=\'underline\'>qui</a> l\'Informativa Privacy, resa ai sensi degli artt. 13 e 14 del Regolamento UE 679/2016, e l\’Informativa per tutti gli Operatori di SIC - Sistema di Informazioni Creditizie -, resa ai sensi dell’art. 6 del Codice di Condotta per i Sistemi Informativi gestiti da soggetti privati in tema di crediti al consumo, affidabilità e puntualità nei pagamenti, consapevole che, in mancanza di tale conferma, Cofidis non potrà dar corso alle operazioni o servizi richiesti. Puoi gestire i seguenti consensi facoltativi:'
                "></div>
            </RadioButton>
          </div>
          <div class="mb-6">
            <RadioButton v-model="privacy2" :values="yesNoOptions"
              :error="v$.privacy2.$error || (privacy2 !== null && !privacy2)" :error-message="v$.privacy2.$error ? Validator.message(v$.privacy2) : val2
                " data-testid="privacy2">
              <div class="w-auto mb-2 text-sm" v-html="'Acconsento al trattamento dei miei dati personali per finalità di marketing per prodotti e servizi di Cofidis, di società appartenenti al Gruppo di Cofidis ed al Gruppo Crédit Mutuel, o di società terze'
                "></div>
            </RadioButton>
          </div>
          <div class="mb-6">
            <RadioButton v-model="privacy3" :values="yesNoOptions"
              :error="v$.privacy3.$error || (privacy3 !== null && !privacy3)" :error-message="v$.privacy3.$error ? Validator.message(v$.privacy3) : val3
                " data-testid="privacy3">
              <div class="w-auto mb-2 text-sm" v-html="'Acconsento al trattamento dei miei dati personali per finalità di profilazione delle mie preferenze, caratteristiche, abitudini o scelte di consumo al fine di ricevere offerte, promozioni ed informazioni che tengano conto dei miei interessi'
                "></div>
            </RadioButton>
          </div>
          <div class="mb-6">
            <RadioButton v-model="privacy4" :values="yesNoOptions"
              :error="v$.privacy4.$error || (privacy4 !== null && !privacy4)" :error-message="v$.privacy4.$error ? Validator.message(v$.privacy4) : val4
                " data-testid="privacy4">
              <div class="w-auto mb-2 text-sm" v-html="'Acconsento al trattamento dei miei dati personali per comunicazione a terzi, a società appartenenti al Gruppo Cofidis ed al Gruppo Crédit Mutuel, per proprie finalità di marketing'
                "></div>
            </RadioButton>
          </div>

          <div class="mt-4">
            <h1 class="pageTitle" v-html="iban.politically_exposed" />
              <div class="my-4">
                <Accordion>
                  <div class="text-base" v-html="iban.pep_definition_banks"></div>
                  <template #items>
                    <div class="py-5 text-gray-600 border-t border-b mt-5 yellowBox" v-html="iban.pep_explanation"></div>
                  </template>
                </Accordion>
              </div>
            <RadioButton v-model="politicallyExposedPerson" :values="[...yesNoOptions].reverse()" :error="v$.politicallyExposedPerson.$error ||
              (politicallyExposedPerson !== null && politicallyExposedPerson)
              " :error-message="v$.politicallyExposedPerson.$error
                  ? Validator.message(v$.politicallyExposedPerson)
                  : politicsVal
                " data-testid="politicallyExposedPerson">
            </RadioButton>
          </div>

        </div>

        <div class="form-footer text-center relative mb-6 ">
          <Button data-testid="emailPrivacySubmit" class="!px-16" type="submit">
            <span>{{ locale?.submit }}</span>
          </Button>
        </div>

        <BackButton class="!pb-2" :event="`goBack`" @go-back="$emit('goBack')" />
      </form>
    </template>
  </BaseForm>
</template>

<style scoped></style>
